import { SYSTEM_PROMPTS } from '../ai/prompts';
import { getApiBaseUrl, isForceMockEnabled } from '../runtime';
import { buildAuthHeader } from '../authEmail';
import { profileContextString } from '../userProfile';

type ParsedResponseBody =
    | { kind: 'json'; data: unknown }
    | { kind: 'text'; data: string };

type LlmTarget = {
    provider: string;
    model: string;
};

const DEFAULT_LLM_TARGET: LlmTarget = {
    provider: 'Google Vertex',
    model: 'Gemini LLM',
};

const normalizeLabel = (value: unknown, fallback: string): string => {
    if (typeof value !== 'string') return fallback;
    const trimmed = value.trim();
    return trimmed || fallback;
};

const getConfiguredLlmTarget = (): LlmTarget => ({
    provider: normalizeLabel(import.meta.env.VITE_CRUCIBLE_LLM_PROVIDER, DEFAULT_LLM_TARGET.provider),
    model: normalizeLabel(import.meta.env.VITE_CRUCIBLE_LLM_MODEL, DEFAULT_LLM_TARGET.model),
});

const getTargetFromResponse = (res: Response, body: ParsedResponseBody, fallback: LlmTarget): LlmTarget => {
    const fromHeaders: LlmTarget = {
        provider: normalizeLabel(res.headers.get('x-llm-provider'), fallback.provider),
        model: normalizeLabel(res.headers.get('x-llm-model'), fallback.model),
    };

    if (body.kind !== 'json' || !body.data || typeof body.data !== 'object') {
        return fromHeaders;
    }

    const jsonBody = body.data as Record<string, unknown>;
    return {
        provider: normalizeLabel(jsonBody.provider, fromHeaders.provider),
        model: normalizeLabel(jsonBody.model, fromHeaders.model),
    };
};

const buildLlmUnreachableMessage = (
    target: LlmTarget,
    code: string | number,
    reason: string,
    details?: string
): string => {
    const detailSuffix = details ? ` Details: ${details}` : '';
    return `${target.provider}, ${target.model} not reachable due to ${reason} (Error ${code})${detailSuffix}`;
};

const formatLlmTarget = (target: LlmTarget): string => `LLM: ${target.provider} · ${target.model}`;

const appendLlmTarget = (message: string, target: LlmTarget): string => {
    const trimmed = message.trim();
    if (!trimmed) return formatLlmTarget(target);
    const signature = `${target.provider} · ${target.model}`;
    if (trimmed.includes(signature) || trimmed.includes('LLM:')) return trimmed;
    return `${trimmed} (${formatLlmTarget(target)})`;
};

const isAbortError = (err: unknown): boolean => {
    if (typeof DOMException !== 'undefined' && err instanceof DOMException && err.name === 'AbortError') return true;
    if (!(err instanceof Error)) return false;
    return err.name === 'AbortError' || err.message.toLowerCase().includes('aborted');
};

const isNetworkishError = (err: unknown): boolean => {
    if (!(err instanceof Error)) return false;
    const msg = err.message.toLowerCase();
    return msg.includes('failed to fetch') || msg.includes('networkerror') || msg.includes('fetch failed') || msg.includes('load failed');
};

export interface CrucibleCard {
    id: string;
    type: 'STRATEGY' | 'PEOPLE' | 'PROCESS' | 'TECH' | 'DATA';
    title: string;
    description: string;
    confidence: number;
    urgency?: number;
    importance?: number;
    aiAnalysis?: string[];
}

export interface WorkingDoc {
    hypotheses: string[];
    constraints?: {
        primary?: string;
    };
    tests?: string[];
    evidence: string[];
    openQuestions: string[];
    cards: CrucibleCard[];
    emotionalVector?: {
        detected_emotion: string;
        underlying_need: string;
        trigger_phrase?: string;
        resonance_strategy?: string;
    };
    meta?: {
        sessionId?: string;
        questionCount: number;
        maxQuestions: number;
    };
}

export type AgentPersona = 'ANALYST' | 'PARTNER';

export interface CrucibleProcessInputOptions {
    agentId?: string;
    agentContext?: string;
}

const CARD_TYPES = ['STRATEGY', 'PEOPLE', 'PROCESS', 'TECH', 'DATA'] as const;
const STOP_WORDS = new Set([
    'a',
    'an',
    'and',
    'for',
    'from',
    'in',
    'into',
    'my',
    'of',
    'on',
    'our',
    'the',
    'to',
    'with',
    'your',
]);

const normalizeCardType = (value: unknown): CrucibleCard['type'] => {
    const upper = String(value ?? '').trim().toUpperCase();
    if (CARD_TYPES.includes(upper as CrucibleCard['type'])) return upper as CrucibleCard['type'];

    const aliases: Record<string, CrucibleCard['type']> = {
        CULTURE: 'PEOPLE',
        DATA: 'DATA',
        FLOW: 'PROCESS',
        ORCHESTRATOR: 'STRATEGY',
        PEOPLE: 'PEOPLE',
        PROCESS: 'PROCESS',
        STRATEGY: 'STRATEGY',
        TECH: 'TECH',
        TECHNOLOGY: 'TECH',
    };

    return aliases[upper] ?? 'PROCESS';
};

const toScale10 = (value: unknown, fallback: number) => {
    const numeric = typeof value === 'number' && Number.isFinite(value) ? value : fallback;
    const scaled = numeric > 10 ? numeric / 10 : numeric;
    return Math.max(0, Math.min(10, Math.round(scaled)));
};

const clipText = (value: unknown, fallback: string): string => {
    if (typeof value !== 'string') return fallback;
    const trimmed = value.replace(/\s+/g, ' ').trim();
    return trimmed || fallback;
};

const stemToken = (token: string): string => {
    if (token.length > 5 && token.endsWith('ing')) return token.slice(0, -3);
    if (token.length > 4 && token.endsWith('ed')) return token.slice(0, -2);
    if (token.length > 4 && token.endsWith('es')) return token.slice(0, -2);
    if (token.length > 3 && token.endsWith('s')) return token.slice(0, -1);
    return token;
};

const tokenizeCardText = (value: string): string[] =>
    value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .split(' ')
        .map(token => stemToken(token.trim()))
        .filter(token => token.length > 1 && !STOP_WORDS.has(token));

const overlapRatio = (left: Set<string>, right: Set<string>): number => {
    if (left.size === 0 || right.size === 0) return 0;
    let shared = 0;
    for (const token of left) {
        if (right.has(token)) shared += 1;
    }
    return shared / Math.min(left.size, right.size);
};

const isSubset = (subset: Set<string>, superset: Set<string>): boolean => {
    if (subset.size === 0) return false;
    for (const token of subset) {
        if (!superset.has(token)) return false;
    }
    return true;
};

const choosePreferredTitle = (current: string, incoming: string): string => {
    const currentTokens = new Set(tokenizeCardText(current));
    const incomingTokens = new Set(tokenizeCardText(incoming));

    if (isSubset(currentTokens, incomingTokens)) return incoming;
    if (isSubset(incomingTokens, currentTokens)) return current;
    return incoming.length > current.length ? incoming : current;
};

const choosePreferredDescription = (current: string, incoming: string): string => {
    if (!current) return incoming;
    if (!incoming) return current;
    return incoming.length > current.length ? incoming : current;
};

const mergeAnalysis = (current?: string[], incoming?: string[]): string[] => {
    const seen = new Set<string>();
    const merged: string[] = [];

    for (const point of [...(current ?? []), ...(incoming ?? [])]) {
        const normalized = clipText(point, '');
        if (!normalized) continue;
        const key = normalized.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push(normalized);
    }

    return merged.slice(0, 6);
};

const cardFingerprint = (card: Pick<CrucibleCard, 'title' | 'description'>): string => {
    const tokens = tokenizeCardText(`${clipText(card.title, '')} ${clipText(card.description, '')}`);
    return Array.from(new Set(tokens)).sort().join('|');
};

const mergeQuestions = (current: string[], incoming?: string): string[] => {
    if (!incoming) return current;
    const normalizedIncoming = clipText(incoming, '');
    if (!normalizedIncoming) return current;

    const seen = new Set<string>();
    const merged: string[] = [];
    for (const question of [normalizedIncoming, ...current]) {
        const normalized = clipText(question, '');
        if (!normalized) continue;
        const key = normalized.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push(normalized);
    }
    return merged.slice(0, 3);
};

const mergeProfileContext = (base: string, extra?: string): string => {
    const normalizedBase = clipText(base, '');
    const normalizedExtra = clipText(extra, '');
    if (!normalizedExtra) return normalizedBase;
    if (!normalizedBase) return normalizedExtra;
    if (normalizedBase.includes(normalizedExtra)) return normalizedBase;
    return `${normalizedBase}\n\n${normalizedExtra}`;
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
    if (!value || typeof value !== 'object') return null;
    return value as Record<string, unknown>;
};

const getStringField = (obj: Record<string, unknown> | null, key: string): string => {
    if (!obj) return '';
    const value = obj[key];
    return typeof value === 'string' ? value : '';
};

const getStringArrayField = (obj: Record<string, unknown> | null, key: string): string[] => {
    if (!obj) return [];
    const value = obj[key];
    if (!Array.isArray(value)) return [];
    return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
};

const isSameCardIntent = (current: CrucibleCard, incoming: CrucibleCard): boolean => {
    if (current.type !== incoming.type) return false;

    const currentTitle = clipText(current.title, current.id).toLowerCase();
    const incomingTitle = clipText(incoming.title, incoming.id).toLowerCase();
    if (currentTitle === incomingTitle) return true;
    if (currentTitle.includes(incomingTitle) || incomingTitle.includes(currentTitle)) return true;

    const currentTitleTokens = new Set(tokenizeCardText(current.title));
    const incomingTitleTokens = new Set(tokenizeCardText(incoming.title));
    const titleOverlap = overlapRatio(currentTitleTokens, incomingTitleTokens);
    if (titleOverlap >= 0.8) return true;

    const currentAllTokens = new Set(
        tokenizeCardText(`${current.title} ${current.description} ${(current.aiAnalysis ?? []).join(' ')}`)
    );
    const incomingAllTokens = new Set(
        tokenizeCardText(`${incoming.title} ${incoming.description} ${(incoming.aiAnalysis ?? []).join(' ')}`)
    );

    return titleOverlap >= 0.5 && overlapRatio(currentAllTokens, incomingAllTokens) >= 0.7;
};

const normalizeCard = (card: CrucibleCard): CrucibleCard => ({
    ...card,
    id: clipText(card.id, `card-${Date.now()}`),
    type: normalizeCardType(card.type),
    title: clipText(card.title, 'Analysis Pending'),
    description: clipText(card.description, card.title || 'Analysis Pending'),
    confidence: typeof card.confidence === 'number' && Number.isFinite(card.confidence) ? card.confidence : 0.8,
    urgency: toScale10(card.urgency, 5),
    importance: toScale10(card.importance, 5),
    aiAnalysis: mergeAnalysis(card.aiAnalysis),
});

/**
 * Merge semantically similar cards so repeated model outputs do not flood the board.
 */
export const normalizeAndMergeCards = (cards: CrucibleCard[]): CrucibleCard[] => {
    const merged: CrucibleCard[] = [];

    for (const rawCard of cards) {
        const card = normalizeCard(rawCard);
        const existing = merged.find(candidate => isSameCardIntent(candidate, card));
        const sameFingerprint = merged.find(candidate => cardFingerprint(candidate) === cardFingerprint(card));
        const target = existing ?? sameFingerprint;

        if (!target) {
            merged.push(card);
            continue;
        }

        target.title = choosePreferredTitle(target.title, card.title);
        target.description = choosePreferredDescription(target.description, card.description);
        target.urgency = Math.round(((target.urgency ?? 5) + (card.urgency ?? 5)) / 2);
        target.importance = Math.round(((target.importance ?? 5) + (card.importance ?? 5)) / 2);
        target.confidence = Math.max(target.confidence, card.confidence);
        target.aiAnalysis = mergeAnalysis(target.aiAnalysis, card.aiAnalysis);
        if (!existing && sameFingerprint && card.type === 'STRATEGY') {
            target.type = card.type;
        }
    }

    // Rank-based spread: amplify score differences so cards visually separate.
    // Top-priority card keeps its extreme scores; lower-ranked cards are stretched
    // AWAY from it, ensuring minimum visual separation on the Eisenboard.
    if (merged.length >= 2) {
        // Sort by combined priority (u+i) descending — the "hottest" card is rank 0.
        const sorted = [...merged].sort((a, b) =>
            ((b.urgency ?? 5) + (b.importance ?? 5)) - ((a.urgency ?? 5) + (a.importance ?? 5))
        );

        // The top card stays as-is. For every other card, stretch its distance from
        // the top card's scores — this makes lower-ranked cards migrate toward other
        // quadrants and away from the #1 card.
        const topU = sorted[0].urgency ?? 5;
        const topI = sorted[0].importance ?? 5;
        const n = sorted.length;

        for (let rank = 1; rank < n; rank++) {
            const card = sorted[rank];
            const rawU = card.urgency ?? 5;
            const rawI = card.importance ?? 5;

            // Push factor increases with rank: rank 1 gets gentle push, rank n-1 gets strongest.
            const pushFactor = 0.3 + 0.5 * (rank / (n - 1));

            // Push urgency away from #1's urgency (softer 0.4x to prevent axis collapse when top has extreme U)
            const deltaU = rawU - topU;
            card.urgency = Math.round(Math.max(0, Math.min(10, rawU + deltaU * pushFactor * 0.4)));

            // Push importance away from #1's importance (0.7x to prevent extreme collapse)
            const deltaI = rawI - topI;
            card.importance = Math.round(Math.max(0, Math.min(10, rawI + deltaI * pushFactor * 0.7)));
        }
    }

    return merged;
};

export class CrucibleEngine {
    private doc!: WorkingDoc;
    private conversationHistory: string[] = [];

    constructor() {
        this.reset();
    }

    public getWorkingDoc(): WorkingDoc {
        return this.doc;
    }

    public getConversationHistory(): string[] {
        return this.conversationHistory;
    }

    /**
     * Main entry point for processing user input through the Crucible.
     */
    public async processInput(input: string, persona: AgentPersona = 'ANALYST', options: CrucibleProcessInputOptions = {}): Promise<{
        response: string;
        updatedDoc: WorkingDoc;
        error?: string;
        mockMode?: boolean;
    }> {
        const mockMode = isForceMockEnabled();
        const apiBaseUrl = getApiBaseUrl();
        const configuredLlmTarget = getConfiguredLlmTarget();

        const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

        const readResponseBody = async (res: Response): Promise<ParsedResponseBody> => {
            const contentType = res.headers.get('content-type') || '';
            try {
                if (contentType.includes('application/json')) {
                    return { kind: 'json', data: await res.json() };
                }
            } catch {
                // fall through
            }

            try {
                return { kind: 'text', data: await res.text() };
            } catch {
                return { kind: 'text', data: '' };
            }
        };

        const shouldRetryStatus = (status: number) => {
            // 409: idempotency in-progress; treat as transient.
            // 429/5xx: transient infra/cost-guard conditions.
            return status === 409 || status === 429 || status === 502 || status === 503 || status === 504;
        };

        const postWithRetry = async (url: string, body: unknown) => {
            const maxAttempts = 3;
            let lastRes: Response | null = null;
            let lastErr: unknown = null;

            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                const controller = new AbortController();
                const timeoutMs = 10000;
                const timeout = setTimeout(() => controller.abort(), timeoutMs);

                try {
                    const res = await fetch(url, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', ...buildAuthHeader() },
                        body: JSON.stringify(body),
                        signal: controller.signal,
                    });
                    lastRes = res;

                    if (res.ok || !shouldRetryStatus(res.status) || attempt === maxAttempts) {
                        return res;
                    }

                    // Exponential backoff with a little jitter.
                    const base = 200 * Math.pow(2, attempt-1);
                    const jitter = Math.floor(Math.random() * 100);
                    await sleep(base + jitter);
                } catch (err) {
                    lastErr = err;
                    // For backend outages, fail fast so UI can exit gracefully.
                    if (isAbortError(err) || isNetworkishError(err)) {
                        throw err;
                    }
                    if (attempt === maxAttempts) throw err;
                    const base = 200 * Math.pow(2, attempt-1);
                    const jitter = Math.floor(Math.random() * 100);
                    await sleep(base + jitter);
                } finally {
                    clearTimeout(timeout);
                }
            }

            if (lastRes) return lastRes;
            throw lastErr;
        };

        // Ensure we have a session ID
        if (!this.doc.meta?.sessionId) {
            this.doc.meta = {
                ...this.doc.meta,
                sessionId: `sess-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                questionCount: 0,
                maxQuestions: 5
            };
        }

        try {
            let data: Record<string, unknown> = {};

            // API call (Vite dev proxy routes /api/* to backend).
            const res = await postWithRetry(`${apiBaseUrl}/decomposer/session`, {
                session_id: this.doc.meta.sessionId,
                question: 'GAMBIT_INPUT',
                answer: input,
                profile_context: mergeProfileContext(profileContextString(), options.agentContext),
                conversation_history: this.conversationHistory.slice(-10),
            });

            if (!res.ok) {
                const body = await readResponseBody(res);
                const llmTarget = getTargetFromResponse(res, body, configuredLlmTarget);
                const messageFromServer = (() => {
                    if (body.kind === 'json') {
                        const maybe = asRecord(body.data);
                        return getStringField(maybe, 'message') || getStringField(maybe, 'error');
                    }
                    if (body.kind === 'text') return body.data;
                    return '';
                })();
                const detailFromServer = (() => {
                    if (body.kind === 'json') {
                        const maybe = asRecord(body.data);
                        return getStringField(maybe, 'detail');
                    }
                    return '';
                })();

                const trimmed = (messageFromServer || '').toString().trim();
                const short = trimmed.length > 280 ? trimmed.slice(0, 277) + '...' : trimmed;
                const detailTrimmed = detailFromServer.trim();
                const detailShort = detailTrimmed.length > 280 ? detailTrimmed.slice(0, 277) + '...' : detailTrimmed;

                if (res.status === 501) {
                    throw new Error(
                        buildLlmUnreachableMessage(
                            llmTarget,
                            501,
                            'feature being disabled on the server',
                            short || 'Set JARVIS_PD_ENABLED=true and restart the API.'
                        )
                    );
                }

                if (res.status >= 500) {
                    throw new Error(
                        buildLlmUnreachableMessage(
                            llmTarget,
                            res.status,
                            'backend being down',
                            detailShort || (body.kind === 'text' ? short : undefined)
                        )
                    );
                }

                // Map status codes to human-readable, actionable reasons
                const reasonForStatus = (status: number, msg: string): string => {
                    const lower = msg.toLowerCase();
                    if (status === 429) {
                        if (lower.includes('daily')) return 'daily rate limit';
                        if (lower.includes('busy') || lower.includes('concurrent') || lower.includes('inflight'))
                            return 'concurrent request limit';
                        return 'rate limiting';
                    }
                    if (status === 402) return 'session limit reached';
                    if (status === 503) return 'service temporarily unavailable';
                    if (status === 401 || status === 403) return 'authentication / authorization';
                    if (status === 400) return 'invalid request';
                    return `an error (HTTP ${status})`;
                };

                throw new Error(
                    buildLlmUnreachableMessage(
                        llmTarget,
                        res.status,
                        reasonForStatus(res.status, short),
                        short || undefined
                    )
                );
            }

            data = asRecord(await res.json()) ?? {};

            // Map API response to WorkingDoc
            // Track conversation turn
            const speaker = clipText(options.agentId, persona === 'PARTNER' ? 'Nova' : 'Specialist');
            this.conversationHistory.push(`User→${speaker}: ${input}`);

            // 1. Facets -> Cards (MERGE with existing, don't replace)
            const rawFacets = Array.isArray(data.facets) ? data.facets : [];
            if (rawFacets.length) {
                const incomingCards: CrucibleCard[] = rawFacets
                    .map((facet): CrucibleCard | null => {
                        const f = asRecord(facet);
                        if (!f) return null;
                        const id = clipText(f.id, `card-${Date.now()}`);
                        const title = clipText(f.title, 'Analysis Pending');
                        return {
                            id,
                            type: normalizeCardType(f.quadrant),
                            title,
                            description: clipText(f.summary, title),
                            confidence: 0.8,
                            urgency: toScale10(f.urgency, 5),
                            importance: toScale10(f.importance, 5),
                            aiAnalysis: getStringArrayField(f, 'aiAnalysis').length
                                ? getStringArrayField(f, 'aiAnalysis')
                                : undefined,
                        };
                    })
                    .filter((card): card is CrucibleCard => card !== null);
                // Merge incoming cards with existing cards to prevent duplicates across turns
                this.doc.cards = normalizeAndMergeCards([...this.doc.cards, ...incomingCards]);
            }

            // 2. Questions -> Open Questions
            const suggestedQuestion = getStringField(data, 'suggested_question');
            if (suggestedQuestion) {
                this.doc.openQuestions = mergeQuestions(this.doc.openQuestions, suggestedQuestion);
            }

            // 2.5 Working Doc Memory (Scientific Method)
            const workingDoc = asRecord(data.working_doc);
            if (workingDoc) {
                const constraints = asRecord(workingDoc.constraints);
                this.doc.constraints = constraints
                    ? { primary: getStringField(constraints, 'primary') || this.doc.constraints?.primary }
                    : this.doc.constraints;
                const hypotheses = getStringArrayField(workingDoc, 'hypotheses');
                const tests = getStringArrayField(workingDoc, 'tests');
                this.doc.hypotheses = hypotheses.length ? hypotheses : this.doc.hypotheses;
                this.doc.tests = tests.length ? tests : this.doc.tests;
            }

            // 3. Emotional Vector
            const emotionalVector = asRecord(data.emotional_vector);
            if (emotionalVector) {
                this.doc.emotionalVector = {
                    detected_emotion: getStringField(emotionalVector, 'emotion') || 'undetermined',
                    underlying_need: getStringField(emotionalVector, 'need') || 'clarity',
                    trigger_phrase: getStringField(emotionalVector, 'trigger') || undefined,
                    resonance_strategy: getStringField(emotionalVector, 'resonance_strategy') || undefined,
                };
            }

            this.doc.meta.questionCount = (this.doc.meta.questionCount || 0) + 1;

            const responseText = suggestedQuestion || "I'm analyzing...";
            // Track AI response in conversation history
            this.conversationHistory.push(`${speaker}: ${responseText}`);

            return {
                response: responseText,
                updatedDoc: this.doc,
                mockMode
            };

        } catch (e) {
            const defaultMsg = e instanceof Error ? e.message : String(e);
            const msg = (() => {
                if (isAbortError(e)) {
                    return buildLlmUnreachableMessage(configuredLlmTarget, 'TIMEOUT', 'backend timeout',
                        'The request took too long. Try again in a moment.');
                }
                if (isNetworkishError(e)) {
                    return buildLlmUnreachableMessage(configuredLlmTarget, 'NETWORK', 'backend being down',
                        'Could not reach the server. Check your connection or try again shortly.');
                }
                // If the error already came from our buildLlmUnreachableMessage formatting
                // (thrown inside the !res.ok block above), pass it through unchanged.
                if (defaultMsg.includes(configuredLlmTarget.provider) || defaultMsg.includes('not reachable due to')) {
                    return defaultMsg;
                }
                // Fallback: still tag the error with model info
                return buildLlmUnreachableMessage(configuredLlmTarget, 'UNKNOWN', 'an unexpected error',
                    defaultMsg);
            })();
            return {
                response: `⚠️ ${msg}`,
                updatedDoc: this.doc,
                error: msg,
                mockMode
            };
        }
    }

    /**
     * Restore a previously-saved working doc (e.g. from localStorage).
     * Lets guest sessions survive page refresh without re-sending the problem.
     */
    public restore(saved: Partial<WorkingDoc> & { conversationHistory?: string[] }) {
        this.doc = {
            hypotheses: saved.hypotheses ?? [],
            constraints: saved.constraints,
            tests: saved.tests,
            evidence: saved.evidence ?? [],
            openQuestions: saved.openQuestions ?? [],
            cards: normalizeAndMergeCards(saved.cards ?? []),
            emotionalVector: saved.emotionalVector,
            meta: saved.meta ?? { questionCount: 0, maxQuestions: 5 },
        };
        this.conversationHistory = saved.conversationHistory ?? [];
    }

    public reset() {
        this.doc = {
            hypotheses: [],
            evidence: [],
            openQuestions: [],
            cards: [],
            meta: {
                questionCount: 0,
                maxQuestions: 5
            }
        };
        this.conversationHistory = [];
    }
}

export const crucible = new CrucibleEngine();
